import dedent from 'ts-dedent'
import {
  Language,
  Provider,
  QueryAndOutputs,
  WorkflowCodeType,
  WorkflowComponentCheckInputValuesParams,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType,
  WorkflowComponentParameterMode,
  WorkflowComponentValue,
  WorkflowParameterValueErrorType
} from '../types'
import { isExpressionValue } from '../utils'
import { DBricksConnection } from '../workflow/connections/databricks'
import {
  SINGLE_OUTPUT_NAME,
  SingleOutputWorkflowComponent
} from './common/singleoutputcomponent'

class CustomPython extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.custompython',
        group: 'Custom',
        title: 'Custom Python Code',
        description: `
        This component executes an arbitrary Python code.

        Use df_a, df_b and df_c as placeholders for dataframes containing the input tables a, b and c respectively,
        and df_output as placeholder for the dataframes with the output table.

        Use $a, $b and $c as placeholders for the input table names
        and $output as placeholder for the output table name.

        `,
        starred: true
      },
      [
        {
          name: 'sourcea',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table a',
          optional: true
        },
        {
          name: 'sourceb',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table b',
          optional: true
        },
        {
          name: 'sourcec',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table c',
          optional: true
        },
        {
          name: 'python_code',
          type: WorkflowComponentParamType.String,
          title: 'Python code',
          mode: WorkflowComponentParameterMode.Multiline,
          // placeholder: 'CALL MY_PROCEDURE($a, 10, $output);',
          allowExpressions: false
        }
      ]
    )
  }

  checkInputValues({
    params,
    resolvedParamValues,
    connection,
    dryRun,
    schemas,
    variables
  }: WorkflowComponentCheckInputValuesParams) {
    const problems = super.checkInputValues({
      params,
      resolvedParamValues,
      connection,
      dryRun,
      schemas,
      variables
    })
    if (
      params.python_code &&
      !isExpressionValue(params.python_code as WorkflowComponentValue)
    ) {
      const missingTables: string[] = []
      const missingDFs: string[] = []
      const value = (params.python_code as WorkflowComponentValue)
        .value as string
      for (const source of ['sourcea', 'sourceb', 'sourcec']) {
        if (
          value &&
          value.includes(`$${source[source.length - 1]}`) &&
          !resolvedParamValues[source]
        ) {
          missingTables.push(source)
        }
        const re = RegExp(`\\bdf_${source[source.length - 1]}\\b`, 'g')
        if (value && re.test(value) && !resolvedParamValues[source]) {
          missingDFs.push(source)
        }
      }
      if (missingTables.length > 0) {
        problems.push({
          parameter: 'python_code',
          type: WorkflowParameterValueErrorType.WrongValue,
          message: `Table${missingTables.length > 1 ? 's' : ''} ${missingTables
            .map((t) => `"${t[t.length - 1]}"`)
            .join(', ')} used in code but not provided`
        })
      }
      if (missingDFs.length > 0) {
        problems.push({
          parameter: 'python_code',
          type: WorkflowParameterValueErrorType.WrongValue,
          message: `Dataframe${missingTables.length > 1 ? 's' : ''} ${missingDFs
            .map((t) => `"df_${t[t.length - 1]}"`)
            .join(', ')} used in code but the corresponding tables not provided`
        })
      }
    }
    return problems
  }

  async getCode({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs> {
    const dryRun = codeType === WorkflowCodeType.DryRun
    const tablename = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      SINGLE_OUTPUT_NAME,
      codeType
    )
    const dryrunTablename = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      SINGLE_OUTPUT_NAME,
      WorkflowCodeType.DryRun
    )
    const dryrunQuery = dedent`DROP TABLE IF EXISTS ${connection.quoteName(
      dryrunTablename
    )};
${connection.getCreateTempTableQuery(
  dryrunTablename,
  `SELECT * FROM ${connection.quoteName(tablename)}`,
  WorkflowCodeType.DryRun,
  null
)}
      `
    let query = ''
    if (dryRun) {
      // do nothing and return empty string, to avoid running the component.
      // The full cicle of the component will generate the dryrun table
    } else {
      let python_code = (params.python_code as string) ?? ''

      // replace the placeholders with the actual table names
      if (connection.type === Provider.DatabricksRest) {
        python_code = python_code
          .replace(
            /`\$a`/g,
            (params.sourcea as string)
              ?.split('.')
              .map((x: string) => `\`${x}\``)
              .join('.')
          )
          .replace(
            /`\$b`/g,
            (params.sourceb as string)
              ?.split('.')
              .map((x: string) => `\`${x}\``)
              .join('.')
          )
          .replace(
            /`\$c`/g,
            (params.sourcec as string)
              ?.split('.')
              .map((x: string) => `\`${x}\``)
              .join('.')
          )
          .replace(
            /`\$output`/g,
            tablename
              ?.split('.')
              .map((x: string) => `\`${x}\``)
              .join('.')
          )
      }
      python_code = python_code
        .replace(/\$a/g, params.sourcea as string)
        .replace(/\$b/g, params.sourceb as string)
        .replace(/\$c/g, params.sourcec as string)
        .replace(/\$output/g, tablename)

      const createsTable = /\bdf_output\b/g.test(params.python_code as string)
      const loadTableA = /\bdf_a\b/g.test(params.python_code as string)
      const loadTableB = /\bdf_b\b/g.test(params.python_code as string)
      const loadTableC = /\bdf_c\b/g.test(params.python_code as string)

      // load the input tables into dataframes if they are used in the code
      if (loadTableA) {
        query += `df_a = spark.sql("SELECT * FROM ${connection.quoteName(
          params.sourcea as string
        )}")\n`
      }

      if (loadTableB) {
        query += `df_b = spark.sql("SELECT * FROM ${connection.quoteName(
          params.sourceb as string
        )}")\n`
      }

      if (loadTableC) {
        query += `df_c = spark.sql("SELECT * FROM ${connection.quoteName(
          params.sourcec as string
        )}")\n`
      }

      query += `${python_code}\n`

      if (createsTable) {
        // write the dataframe df_output to the output table
        query += `df_output.write.mode("overwrite").saveAsTable("${connection.quoteName(
          tablename as string
        )}")\n`
        // create the dryrun table
        query += (connection as DBricksConnection).wrapSQLCodeAsPythonCall(
          dryrunQuery
        )

        query = connection.wrapCodeInIfTableExistsBlock(
          query,
          [connection.quoteName(tablename as string)],
          codeType
        )

        query += `\nspark.sql('''ANALYZE TABLE ${connection.quoteName(
          tablename as string
        )} COMPUTE STATISTICS''')\n`
      }
    }

    return {
      query,
      outputs: { result: tablename }
    }
  }

  language(provider: Provider): Language {
    return Language.Python
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getVersion(provider: Provider): string {
    return '1'
  }
  supportedProviders(): Provider[] {
    return [Provider.DatabricksRest]
  }
}

export { CustomPython }
